/* eslint-disable */

import * as runtime from '../runtime';
import type {
  AppointmentPaymentOptionsDto,
  AppointmentRegistrationContextDto,
  GetAppointmentRegistrationContextRequestDto,
  GetOAuthLinkResponse,
  MonthIndicatorsResponseDto,
  ScheduleAvailabilityDaysDto,
  ScheduleConfigurationDto,
  ScheduleTimeslotDto,
  ScheduledAppointmentRequestDto,
} from '../models';

export interface GetScheduleConfigurationsForOrgRequest {
  orgIds: string;
  includeArchived?: boolean;
}

export interface ScheduleAppointmentRequest {
  orgId: number;
  providerUserId: number;
  scheduledAppointmentRequestDto: ScheduledAppointmentRequestDto;
}

export interface GetAppointmentRegistrationContextRequest {
  orgId: number;
  providerUserId: number;
  appointmentTypeId: string;
  getAppointmentRegistrationContextRequestDto: GetAppointmentRegistrationContextRequestDto;
}

export interface GetPaymentOptionsNoAuthScheduleRequest {
  orgId: number;
  providerUserId: number;
  appointmentTypeId: string;
  appointmentDate?: string;
}

export interface GetScheduleAvailabilitiesRequest {
  orgId: number;
  providerUserId: number;
  appointmentTypeId?: string;
  startDate?: string;
  endDate?: string;
}

export interface GetScheduleAvailabilityDaysRequest {
  orgId: number;
  providerUserId: number;
  appointmentTypeId?: string;
  startDate?: string;
  endDate?: string;
  day?: string;
  timeZone?: string;
}

export interface GetMonthIndicatorsRequest {
  orgId: number;
  year?: number;
  month?: number;
  locationIds?: string;
}

export class ScheduleApi extends runtime.BaseAPI {

  async getScheduleConfigurationsForOrg(requestParameters: GetScheduleConfigurationsForOrgRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<Array<ScheduleConfigurationDto>> {
    const queryParameters: any = {};

    if (requestParameters.orgIds !== undefined) {
      queryParameters['orgIds'] = requestParameters.orgIds;
    }

    if (requestParameters.includeArchived !== undefined) {
      queryParameters['includeArchived'] = requestParameters.includeArchived;
    }

    const response = await this.request({
      path: '/schedule/all/configurations',
      method: 'GET',
      headers: {},
      query: queryParameters,
    }, initOverrides);
    return response.json();
  }

  async scheduleAppointment(requestParameters: ScheduleAppointmentRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<GetOAuthLinkResponse> {
    const headerParameters: runtime.HTTPHeaders = {};

    headerParameters['Content-Type'] = 'application/json';

    const response = await this.request({
      path: `/schedule/${encodeURIComponent(String(requestParameters.orgId))}/${encodeURIComponent(String(requestParameters.providerUserId))}/appointments-no-auth`,
      method: 'POST',
      headers: headerParameters,
      query: {},
      body: requestParameters.scheduledAppointmentRequestDto,
    }, initOverrides);
    return response.json();
  }

  async getAppointmentRegistrationContext(requestParameters: GetAppointmentRegistrationContextRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<AppointmentRegistrationContextDto> {
    const headerParameters: runtime.HTTPHeaders = {};

    headerParameters['Content-Type'] = 'application/json';

    const response = await this.request({
      path: `/schedule/${encodeURIComponent(String(requestParameters.orgId))}/${encodeURIComponent(String(requestParameters.providerUserId))}/appointment-type/${encodeURIComponent(String(requestParameters.appointmentTypeId))}/registration-context`,
      method: 'POST',
      headers: headerParameters,
      query: {},
      body: requestParameters.getAppointmentRegistrationContextRequestDto,
    }, initOverrides);
    return response.json();
  }

  async getPaymentOptionsNoAuth(requestParameters: GetPaymentOptionsNoAuthScheduleRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<AppointmentPaymentOptionsDto> {
    const queryParameters: any = {};

    if (requestParameters.appointmentDate !== undefined) {
      queryParameters['appointmentDate'] = requestParameters.appointmentDate;
    }

    const response = await this.request({
      path: `/schedule/${encodeURIComponent(String(requestParameters.orgId))}/${encodeURIComponent(String(requestParameters.providerUserId))}/appointment-type/${encodeURIComponent(String(requestParameters.appointmentTypeId))}/payment-options-noauth`,
      method: 'POST',
      headers: {},
      query: queryParameters,
    }, initOverrides);
    return response.json();
  }

  async getScheduleAvailabilities(requestParameters: GetScheduleAvailabilitiesRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<Array<ScheduleTimeslotDto>> {
    const queryParameters: any = {};

    if (requestParameters.appointmentTypeId !== undefined) {
      queryParameters['appointmentTypeId'] = requestParameters.appointmentTypeId;
    }

    if (requestParameters.startDate !== undefined) {
      queryParameters['startDate'] = requestParameters.startDate;
    }

    if (requestParameters.endDate !== undefined) {
      queryParameters['endDate'] = requestParameters.endDate;
    }

    const response = await this.request({
      path: `/schedule/${encodeURIComponent(String(requestParameters.orgId))}/${encodeURIComponent(String(requestParameters.providerUserId))}/availabilities`,
      method: 'GET',
      headers: {},
      query: queryParameters,
    }, initOverrides);
    return response.json();
  }

  async getScheduleAvailabilityDays(requestParameters: GetScheduleAvailabilityDaysRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<ScheduleAvailabilityDaysDto> {
    const queryParameters: any = {};

    if (requestParameters.appointmentTypeId !== undefined) {
      queryParameters['appointmentTypeId'] = requestParameters.appointmentTypeId;
    }

    if (requestParameters.startDate !== undefined) {
      queryParameters['startDate'] = requestParameters.startDate;
    }

    if (requestParameters.endDate !== undefined) {
      queryParameters['endDate'] = requestParameters.endDate;
    }

    if (requestParameters.day !== undefined) {
      queryParameters['day'] = requestParameters.day;
    }

    if (requestParameters.timeZone !== undefined) {
      queryParameters['timeZone'] = requestParameters.timeZone;
    }

    const response = await this.request({
      path: `/schedule/${encodeURIComponent(String(requestParameters.orgId))}/${encodeURIComponent(String(requestParameters.providerUserId))}/availability-days`,
      method: 'GET',
      headers: {},
      query: queryParameters,
    }, initOverrides);
    return response.json();
  }

  async getMonthIndicators(requestParameters: GetMonthIndicatorsRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<MonthIndicatorsResponseDto> {
    const queryParameters: any = {};

    if (requestParameters.year !== undefined) {
      queryParameters['year'] = requestParameters.year;
    }

    if (requestParameters.month !== undefined) {
      queryParameters['month'] = requestParameters.month;
    }

    if (requestParameters.locationIds !== undefined) {
      queryParameters['locationIds'] = requestParameters.locationIds;
    }

    const response = await this.request({
      path: `/schedule/${encodeURIComponent(String(requestParameters.orgId))}/month-indicators`,
      method: 'GET',
      headers: {},
      query: queryParameters,
    }, initOverrides);
    return response.json();
  }

}
